import { useQuery } from '@tanstack/react-query'
import { useAuth } from 'context/authentication/useAuth'
import { useMemo } from 'react'
import apiService from 'services/api'
import { USER_TYPE_ADMIN, USER_TYPE_STANDARD } from 'models/user'

interface RSMOption {
  label: string
  value: number
}

const RSM_ELIGIBLE_TYPES = new Set([USER_TYPE_ADMIN, USER_TYPE_STANDARD])

export function useRSMEligibleUsers(
  enabled: boolean,
  excludeUserId?: number | null
) {
  const api = apiService()
  const { user } = useAuth()

  const { data: rsmEligibleUsers } = useQuery({
    queryKey: ['rsm-eligible-users'],
    queryFn: async () => {
      return await api.getRSMEligibleUsers()
    },
    enabled,
  })

  const rsmOptions = useMemo<RSMOption[]>(() => {
    if (!rsmEligibleUsers) return []
    const options = rsmEligibleUsers
      .filter((u) => u.id !== excludeUserId)
      .map((u) => ({
        label: `${u.first_name} ${u.last_name}`,
        value: u.id,
      }))

    // Ensure the current user is available as an RSM option if eligible
    // (they may be filtered out by the API, e.g. superusers in dev)
    if (
      user &&
      user.id !== excludeUserId &&
      RSM_ELIGIBLE_TYPES.has(user.user_type as typeof USER_TYPE_ADMIN) &&
      !options.some((o) => o.value === user.id)
    ) {
      options.unshift({
        label: `${user.first_name} ${user.last_name}`,
        value: user.id,
      })
    }

    return options
  }, [rsmEligibleUsers, excludeUserId, user])

  return { rsmOptions, rsmEligibleUsers }
}
